<template>
    <div class="mx-auto p-2">
        <!-- 页面标题 -->
        <div class="mb-8">
            <h2 class="text-3xl font-bold text-base-content flex items-center justify-start gap-2">
                权限管理
            </h2>
            <p class="text-base-content/60 text-sm mt-2">为你的音乐收藏分配合适的角色</p>
        </div>

        <!-- 收藏家区域 (管理员) -->
        <div class="card bg-base-100 shadow-xl border border-primary/20 mb-6 relative overflow-hidden" 
             @drop="handleDrop($event, 'admin')" 
             @dragover.prevent 
             @dragenter.prevent>
            <!-- 装饰条 -->
            <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary"></div>
            <div class="card-body">
                <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-4">
                        <div class="text-4xl">🎯</div>
                        <div>
                            <h3 class="text-xl font-bold text-base-content">音乐收藏家</h3>
                            <p class="text-sm text-base-content/70">全权管理整个音乐收藏，拥有所有管理权限</p>
                        </div>
                    </div>
                    <div class="badge badge-primary badge-lg">{{ adminUsers.length }} 位</div>
                </div>
            
                <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
                    <div v-for="user in adminUsers" 
                         :key="user.id" 
                         class="card bg-base-200 hover:bg-base-300 shadow-sm hover:shadow-md transition-all duration-300 cursor-move relative group"
                         draggable="true"
                         @dragstart="handleDragStart($event, user, 'admin')">
                        <div class="card-body p-2 items-center text-center">
                            <div class="avatar relative">
                                <div class="w-10 rounded-full ring ring-primary ring-offset-base-100 ring-offset-1">
                                    <img :src="user.avatar || '/static/dist/img/avator.webp'" :alt="user.name" />
                                </div>
                                <div class="absolute -bottom-0.5 -right-0.5 bg-primary text-primary-content rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                                    🎯
                                </div>
                            </div>
                            <div class="text-[10px] font-medium text-base-content mt-1 truncate w-full">{{ user.name }}</div>
                            <button class="btn btn-circle btn-xs btn-error absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity scale-75" 
                                    @click="removeUser(user.id, 'admin')" 
                                    title="离团">
                                ✕
                            </button>
                        </div>
                    </div>
                    
                    <!-- 招募按钮 -->
                    <div class="card bg-base-200/50 border-2 border-dashed border-base-300 hover:bg-base-200 hover:border-primary/50 transition-all duration-300 cursor-pointer"
                         @click="showRecruitModal('admin')">
                        <div class="card-body p-2 items-center justify-center text-center min-h-[80px]">
                            <div class="text-lg text-base-content/50">➕</div>
                            <div class="text-[10px] font-medium text-base-content/70">招募收藏家</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 听众区域 (员工) -->
        <div class="card bg-base-100 shadow-xl border border-secondary/20 relative overflow-hidden" 
             @drop="handleDrop($event, 'employee')" 
             @dragover.prevent 
             @dragenter.prevent>
            <!-- 装饰条 -->
            <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-secondary to-accent"></div>
            <div class="card-body">
                <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-4">
                        <div class="text-4xl">🎵</div>
                        <div>
                            <h3 class="text-xl font-bold text-base-content">听众席位</h3>
                            <p class="text-sm text-base-content/70">欣赏美妙的音乐乐章，拥有收听音乐的权限</p>
                        </div>
                    </div>
                    <div class="badge badge-secondary badge-lg">{{ employeeUsers.length }} 位</div>
                </div>
                
                <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
                    <div v-for="user in employeeUsers" 
                         :key="user.id" 
                         class="card bg-base-200 hover:bg-base-300 shadow-sm hover:shadow-md transition-all duration-300 cursor-move relative group"
                         draggable="true"
                         @dragstart="handleDragStart($event, user, 'employee')">
                        <div class="card-body p-2 items-center text-center">
                            <div class="avatar relative">
                                <div class="w-10 rounded-full ring ring-secondary ring-offset-base-100 ring-offset-1">
                                    <img :src="user.avatar || '/static/dist/img/avator.webp'" :alt="user.name" />
                                </div>
                                <div class="absolute -bottom-0.5 -right-0.5 bg-secondary text-secondary-content rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                                    🎵
                                </div>
                            </div>
                            <div class="text-[10px] font-medium text-base-content mt-1 truncate w-full">{{ user.name }}</div>
                            <button class="btn btn-circle btn-xs btn-error absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity scale-75" 
                                    @click="removeUser(user.id, 'employee')" 
                                    title="离团">
                                ✕
                            </button>
                        </div>
                    </div>
                    
                    <!-- 招募按钮 -->
                    <div class="card bg-base-200/50 border-2 border-dashed border-base-300 hover:bg-base-200 hover:border-secondary/50 transition-all duration-300 cursor-pointer"
                         @click="showRecruitModal('employee')">
                        <div class="card-body p-2 items-center justify-center text-center min-h-[80px]">
                            <div class="text-lg text-base-content/50">➕</div>
                            <div class="text-[10px] font-medium text-base-content/70">招募听众</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 招募用户弹窗 -->
        <dialog ref="recruitModal" id="recruit_modal" class="modal modal-bottom sm:modal-middle">
            <div class="modal-box bg-base-100">
                <h3 class="text-lg font-bold mb-4 text-base-content">
                    {{ recruitRole === 'admin' ? '🎯 招募收藏家' : '🎵 招募听众' }}
                </h3>
                
                <div class="mb-4">
                    <input type="text" 
                           placeholder="搜索用户..." 
                           v-model="searchKeyword"
                           @input="searchUsers"
                           class="input input-bordered input-sm w-full" />
                </div>
                
                <div class="max-h-60 overflow-y-auto space-y-2">
                    <div v-for="user in availableUsers" 
                         :key="user.id" 
                         class="card bg-base-200 hover:bg-base-300 transition-colors cursor-pointer"
                         @click="selectUser(user)">
                        <div class="card-body p-3">
                            <div class="flex items-center gap-3">
                                <div class="avatar">
                                    <div class="w-10 rounded-full">
                                        <img :src="user.avatar || '/static/dist/img/avator.webp'" :alt="user.name" />
                                    </div>
                                </div>
                                <div class="flex-1">
                                    <div class="font-medium text-base-content">{{ user.name }}</div>
                                    <div class="text-xs text-base-content/60">{{ user.email }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="modal-action">
                    <form method="dialog">
                        <button class="btn btn-sm">取消</button>
                    </form>
                </div>
            </div>
        </dialog>

        <!-- 拖拽时的音符动画 -->
        <div v-if="isDragging" class="floating-notes">
            <div class="note">🎵</div>
            <div class="note">🎶</div>
            <div class="note">♪</div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RolePermission',
    data() {
        return {
            adminUsers: [],
            employeeUsers: [],
            searchKeyword: '',
            recruitRole: 'admin',
            isDragging: false,
            draggedUser: null,
            draggedFromRole: null,
        }
    },
    mounted() {
        this.loadRoleUsers()
    },
    computed: {
        availableUsers() {
            if (this.recruitRole === 'admin') {
                return this.employeeUsers
            } else {
                return this.adminUsers
            }
        }
    },
    methods: {
        loadRoleUsers() {
            this.$api.Task.roleList().then(res => {
                this.adminUsers = res.data.superusers
                this.employeeUsers = res.data.employees
            })
        },
        
        handleDragStart(event, user, fromRole) {
            this.isDragging = true
            this.draggedUser = user
            this.draggedFromRole = fromRole
            event.dataTransfer.effectAllowed = 'move'
        },
        
        handleDrop(event, toRole) {
            event.preventDefault()
            this.isDragging = false
            
            if (this.draggedUser && this.draggedFromRole !== toRole) {
                this.moveUser(this.draggedUser, this.draggedFromRole, toRole)
            }
            
            this.draggedUser = null
            this.draggedFromRole = null
        },
        
        moveUser(user, fromRole, toRole) {
            // 从原角色移除
            if (fromRole === 'admin') {
                this.adminUsers = this.adminUsers.filter(u => u.id !== user.id)
            } else {
                this.employeeUsers = this.employeeUsers.filter(u => u.id !== user.id)
            }
            
            // 添加到新角色
            if (toRole === 'admin') {
                this.adminUsers.push(user)
            } else {
                this.employeeUsers.push(user)
            }
            
            // 通知父组件更新
            this.updateRoleUsers()
            
            // 显示成功消息
            this.$myMsg.notify({
                title: `🎉 ${user.name} 已${toRole === 'admin' ? '晋升为收藏家' : '加入听众团'}！`,
                type: 'success',
                time: 2000,
            })
        },
        
        removeUser(userId, role) {
            const userName = role === 'admin' 
                ? this.adminUsers.find(u => u.id === userId)?.name
                : this.employeeUsers.find(u => u.id === userId)?.name
            
            if (role === 'admin') {
                this.adminUsers = this.adminUsers.filter(u => u.id !== userId)
            } else {
                this.employeeUsers = this.employeeUsers.filter(u => u.id !== userId)
            }
            
            this.updateRoleUsers()
            
            this.$myMsg.notify({
                title: `👋 ${userName} 已离开乐团`,
                type: 'info',
                time: 2000,
            })
        },
        
        showRecruitModal(role) {
            this.recruitRole = role
            this.searchKeyword = ''
            this.searchUsers()
            this.$refs.recruitModal.showModal()
        },
        
        searchUsers() {
            // 过滤已分配的用户
            const assignedUserIds = [...this.adminUsers, ...this.employeeUsers].map(u => u.id)
            
            this.availableUsers = this.availableUsers.filter(user => {
                const matchSearch = !this.searchKeyword || 
                    user.name.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
                    user.email.toLowerCase().includes(this.searchKeyword.toLowerCase())
                
                return matchSearch && !assignedUserIds.includes(user.id)
            })
        },
        
        selectUser(user) {
            if (this.recruitRole === 'admin') {
                this.adminUsers.push(user)
                // 移除已有的员工
                this.employeeUsers = this.employeeUsers.filter(u => u.id !== user.id)
            } else {
                this.employeeUsers.push(user)
                // 移除已有的收藏家
                this.adminUsers = this.adminUsers.filter(u => u.id !== user.id)
            }
            
            this.updateRoleUsers()
            this.$refs.recruitModal.close()
            
            this.$myMsg.notify({
                title: `🎉 欢迎 ${user.name} 加入${this.recruitRole === 'admin' ? '收藏家' : '听众'}团队！`,
                type: 'success',
                time: 2000,
            })
        },
        
        updateRoleUsers() {
            this.$api.Task.updateRoleUsers({
                admin_users: this.adminUsers.map(u => u.id),
                employee_users: this.employeeUsers.map(u => u.id)
            }).then(res => {
                if (res.result) {
                    this.$myMsg.notify({
                    title: '更新成功',
                    type: 'success',
                    time: 2000,
                })
                } else {
                    this.$myMsg.notify({
                        title: '更新失败',
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        }
    }
}
</script>

<style scoped>
/* 拖拽时的音符动画 */
.floating-notes {
    position: fixed;
    top: 50%;
    left: 50%;
    pointer-events: none;
    z-index: 1000;
}

.note {
    position: absolute;
    font-size: 2rem;
    animation: float 2s ease-in-out infinite;
}

.note:nth-child(1) {
    animation-delay: 0s;
    left: -20px;
}

.note:nth-child(2) {
    animation-delay: 0.5s;
    left: 0px;
}

.note:nth-child(3) {
    animation-delay: 1s;
    left: 20px;
}

@keyframes float {
    0%, 100% {
        transform: translateY(0px) rotate(0deg);
        opacity: 0.7;
    }
    50% {
        transform: translateY(-20px) rotate(10deg);
        opacity: 1;
    }
}

/* 用户头像卡片悬停时显示删除按钮 */
.group:hover .btn-error {
    opacity: 1;
}
</style> 